import React from 'react';
import type {MessageDescriptor} from 'react-intl';
import {defineMessages} from 'react-intl';
import type {
    DateRangeFilterValue,
    FilterGroupNewFilter,
    FilterProps,
    FiltersProps,
    MultiKeyFilterTextValue,
} from '@igp/shared/components/filter';
import {DateRangeFilter, FilterGroupNew, TextInputWithDropdownFilter} from '@igp/shared/components/filter';
import type {AgentFilterSharedProps, AgentFilterValue} from '@igp/shared/features/agent-profile';
import {AgentFilter} from '@igp/shared/features/agent-profile';
import type {CountryFilterSharedProps} from '@igp/shared/features/country';
import {CountryFilter} from '@igp/shared/features/country';
import {useLocale} from '@igp/shared/localization';
import type {Locale} from '@igp/shared/style';
import type {EntityType} from '@models';

import {firstAvailableDate} from '../constants';
import type {PlayerLevelReportV2FilterName, PlayerLevelReportV2FilterViewModel, PlayerLevelReportV2FilterViewModelTextKeys} from '../types';

const localized = defineMessages({
    textFullFilter: {
        id: 'PlayerLevelReportV2Filters_textFullFilter',
        defaultMessage: 'All',
    },
    uid: {
        id: 'PlayerLevelReportV2Filters_userId',
        defaultMessage: 'Player ID',
    },
    countries: {
        id: 'PlayerLevelReportV2Filters_countries',
        defaultMessage: 'Country',
    },
    username: {
        id: 'PlayerLevelReportV2Filters_username',
        defaultMessage: 'Username',
    },
    numberPlaceholder: {
        id: 'PlayerLevelReportV2Filters_numberPlaceholder',
        defaultMessage: 'For example, 123',
    },
    namePlaceholder: {
        id: 'PlayerLevelReportV2Filters_namePlaceholder',
        defaultMessage: 'For example, Antonio',
    },
});

type PlayerLevelReportV2FiltersProps = FiltersProps<PlayerLevelReportV2FilterViewModel, PlayerLevelReportV2FilterName> &
    CountryFilterSharedProps<EntityType> &
    AgentFilterSharedProps<EntityType>;

export function PlayerLevelReportV2Filters({
    countryEntity,
    countryConfig,
    defaultPlayerId,
    isAgent,
    viewType,
    agentProfileEntity,
    model,
    onChange,
    availableFilters,
}: PlayerLevelReportV2FiltersProps) {
    const {locale} = useLocale();

    const availableDateRangeForSelection = {from: firstAvailableDate.toDate()};

    function getTextFilterPlaceholder(option: PlayerLevelReportV2FilterViewModelTextKeys) {
        const placeholderMapper: Record<PlayerLevelReportV2FilterViewModelTextKeys, MessageDescriptor> = {
            uid: localized.numberPlaceholder,
            uid_username: localized.numberPlaceholder,
            username: localized.namePlaceholder,
        };
        return placeholderMapper[option];
    }

    const filters: FilterGroupNewFilter<PlayerLevelReportV2FilterViewModel, PlayerLevelReportV2FilterName>[] = [
        {
            filterName: 'uid_username',
            modelField: 'text',
            component: (props: FilterProps<MultiKeyFilterTextValue<PlayerLevelReportV2FilterViewModelTextKeys>>) => (
                <TextInputWithDropdownFilter
                    options={[
                        {label: localized.textFullFilter, value: 'uid_username'},
                        {label: localized.uid, value: 'uid'},
                        {label: localized.username, value: 'username'},
                    ]}
                    getInputPlaceholder={getTextFilterPlaceholder}
                    width="full"
                    {...props}
                />
            ),
        },
        {
            filterName: 'countries',
            modelField: 'countries',
            component: (props: FilterProps<string[]>) => (
                <CountryFilter inputLabel={localized.countries} countryEntity={countryEntity} config={countryConfig} {...props} />
            ),
        },
        {
            filterName: 'startedAt',
            modelField: 'startedAt',
            component: (props: FilterProps<DateRangeFilterValue>) => (
                <DateRangeFilter
                    {...props}
                    width="full"
                    locale={locale as Locale}
                    availableDateRangeForSelection={availableDateRangeForSelection}
                    filterEntity="report"
                    hasInputTooltip={true}
                />
            ),
        },
        {
            filterName: 'agent',
            modelField: 'agent',
            component: (props: FilterProps<AgentFilterValue>) => (
                <AgentFilter
                    defaultPlayerId={defaultPlayerId}
                    width="full"
                    viewType={viewType}
                    withDownstream={true}
                    agentProfileEntity={agentProfileEntity}
                    isTreeMode={isAgent}
                    {...props}
                />
            ),
        },
    ];

    return (
        <FilterGroupNew
            model={model}
            onChange={onChange}
            mode="update-on-change"
            allFilters={filters}
            availableFilters={availableFilters}
        />
    );
}
